/**
 *
 * Vue.js and Parse.Object
 *
 * Transform Parse.Object into POJO so we can use it in Vue.js
 * Make it possible to use the Parse.Object and sync changes to the model data.
 * Easily $fetch and $save model data.
 *
 * needs:
 * - Lodash
 * - Parse SDK JS
 * - Vue.js
 * 
 **/
import Vue from 'vue'
import Parse from 'parse'
import _ from 'lodash'

function ParseObjectPOJO(obj, cloned) {
    // keep track of changed attributes and attribute names to watch
    // ob: use observable to proxy data
    // sync: variables to set when updating data
    var changed = {}, attrs = {}, data = {}, ob = {}, sync = [];

    if (cloned) {
        if (cloned.data) data = cloned.data;
        if (cloned.attrs) attrs = cloned.attrs;
    }

    var update_data = function(obj) {
            var _data = obj.toJSON();
            delete _data['updatedAt'];
            delete _data['createdAt'];

            // special attributes (clone them):
            _data.objectId = obj.id ? ''+obj.id : undefined;
            _data.className = obj.className ? ''+obj.className : undefined;
            //_data.updatedAt = obj.updatedAt;
            //_data.createdAt = obj.createdAt;

            Object.keys(_data).forEach(function(key) {
                data[key] = _data[key];
            });
            //data = _data;

            sync_handler();
        },
        sync_handler = function() {
            // sync changes to variables (they use defineProperty internally)
            sync.forEach(function(s) {
                Vue.set(s[0], s[1], ob);
            });
        };

    // convert json back to Parse.Object (only changed attributes)
    var parse_obj;
    var toParseObject = function() {
            var use_parse_obj = false;
            if (
                parse_obj &&
                parse_obj.objectId == data.objectId &&
                parse_obj.className == data.className
            ) {
                use_parse_obj = true;
            }
            // https://github.com/parse-community/Parse-SDK-JS/issues/161
            var new_data = {
                objectId: data.objectId,
                className: data.className,
            };
            Object.keys(changed).forEach(function(changed_key) {
                if (use_parse_obj) {
                    // reflect changes on parse_obj
                    parse_obj.set(changed_key, data[changed_key]);
                } else {
                    new_data[changed_key] = data[changed_key];
                }
            });
            changed = {};
            if (use_parse_obj) {
                // sync from Parse.Object to data
                var dirty_keys = parse_obj.dirtyKeys();
                dirty_keys.forEach(function(dirty_key) {
                    data[dirty_key] = parse_obj.get(dirty_key);
                });
                sync_handler();

                return parse_obj;
            } else {
                parse_obj = new Parse.Object(new_data);
                return parse_obj;
            }
        },
        destroy = function() {
            // reset data structures
            ob = {};
            data = {};
            changed = {};
            attrs = {};
            sync = [];
        };

    // can't use normal assignment because it goes to the JSON structure
    Object.defineProperty(ob, '$p', {
        get: toParseObject,
        enumerable: false,
        configurable: false,
    });

    // watch for changes and save changes in 'changed'
    var watch = function(keys) {
            keys.forEach(function(key) {
                if (!data.hasOwnProperty(key)) {
                    data[key] = null;
                }
                Object.defineProperty(ob, key, {
                    get: function() {
                        return data[key];
                    },
                    set: function(val) {
                        if (!changed[key]) {
                            changed[key] = true;
                        }
                        return data[key] = val;
                    },
                    enumerable: true,
                    configurable: true,
                });
            });

            // comment out if you're not using Vue.js
            //     this line is crucial for Vue.js!
            //     without this line it doesn't synchronize with Vue.js
            //     and everything stops working
            new Vue({
                data: function() {
                    return {data: ob.$data};
                },
            });
        },
        add_attrs = function() {
            // call this with all the attribute names you want to watch
            var attrs_names = Array.from(arguments);
            attrs_names.forEach(function(name) {
                attrs[name] = true;
            });
            return watch(attrs_names);
        },
        update_watch = function() {
            watch(Object.keys(data).concat(Object.keys(attrs)));
        };


    // operations
    var self = this;
    var save = function() {
            var p = ob.$p, promise = new Parse.Promise();
            p.save.apply(p, Array.from(arguments)).then(function(data) {
                update(data);
                promise.resolve(ob);
            }, function(err) {
                promise.reject(err);
            });
            return promise;
        },
        fetch = function() {
            var p = ob.$p, promise = new Parse.Promise();
            p.fetch.apply(p, Array.from(arguments)).then(function(data) {
                update(data);
                promise.resolve(ob);
            }, function(err) {
                promise.reject(err);
            });
            return promise;
        },
        _sync = function(target, key) {
            sync.push([target, key]);
            //variable = data;
            // remove line if not using Vue.js
            Vue.set(target, key, ob);
        },
        update = function(new_obj) {
            // if vm.$p.set('somekey', val) was used, tell it here
            // using vm.$update() so we can check it.
            if ((!new_obj) && parse_obj) {
                new_obj = parse_obj;
            } else if (!new_obj) {
                return;
            }
            if (new_obj.hasOwnProperty('$p')) {
                // unfortunately we can't use instanceof bc of ob.defineProperty
                // (would need a bigger refactoring)
                new_obj = new_obj.$p;
            }
            update_data(new_obj);
            update_watch();
        },
        set_acl = function(acl) {
            if (!acl) {
                return (new Parse.ACL(data['ACL'])).toJSON();
            } else if (acl instanceof Parse.ACL) {
                changed['ACL'] = true;
                data.ACL = acl.toJSON();
            }
        },
        clone = function() {
            // use the following line if you don't use Vue.js:
            // return new ParseObjectPOJO(ob.$p);
            return new ParseObjectPOJO(ob.$p, {
                attrs: _.cloneDeep(attrs),
                data: _.cloneDeep(data),
            });
            // TODO: try/except for "Error: Tried to encode an unsaved file."
        },
        get_data = function() {
            return data;
        };

    // internal helper functions

    var add_functions = function(fns) {
        Object.keys(fns).forEach(function(fn) {
            var call = fns[fn];
            Object.defineProperty(ob, fn, {
                get: function() {
                    return call;
                },
                enumerable: false,
                configurable: false,
            });
        });
    }

    // init

    update_data(obj);
    update_watch();

    add_functions({
        '$destroy': destroy,
        '$attrs': add_attrs,
        //
        '$sync': _sync,
        '$update': update,
        '$acl': set_acl,
        '$clone': clone,
        '$data': get_data(),
        //
        '$save': save,
        '$fetch': fetch,
    });
    
    if (cloned) {
        new Vue({
            data: function() {
                return {data: ob.$data};
            },
        });
    }

    return ob;
}


function ParseViewModel(model, attrs) {
    return function(_id) {
        var m;
        if (_id instanceof model) {
            m = _id;
        } else if (typeof(_id) === 'string') {
            m = model.createWithoutData(_id);
        } else {
            m = new model();
        }
        var vm = new ParseObjectPOJO(m);
        vm.$attrs.apply(vm, attrs);
        return vm;
    };
}

export function ParseViewModelFile(parse_file) {
    // new ParseViewModelFile(new ParseFile(...))
    Object.defineProperty(this, 'name', {
        get: function() {
            return parse_file.name();
        },
        enumerable: false,
        configurable: false,
    });
    Object.defineProperty(this, 'url', {
        get: function() {
            return parse_file.url();
        },
        enumerable: false,
        configurable: false,
    });
    Object.defineProperty(this, '$p', {
        get: function() {
            return parse_file;
        },
        enumerable: false,
        configurable: false,
    });
    return this;
}


export default ParseViewModel



// WEBPACK FOOTER //
// ./src/utils/parse-object-pojo.js